import React from "react";
import {
  FaSearch,
  FaRobot,
  FaSatelliteDish,
  FaMobileAlt,
  FaShieldAlt,
  FaChartBar,
  FaPalette,
  FaGlobe,
  FaDatabase,
  FaBookOpen, 
  FaNewspaper, 
  FaFileAlt, 
  FaPenFancy, 
  FaLayerGroup, 
  FaTabletAlt,
  FaArrowUp,
  FaHeadset,
  FaQuoteLeft,
  FaVideo,
  FaFileSignature,
} from "react-icons/fa";
import CountUp from 'react-countup';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Typewriter from 'typewriter-effect';
import { useNavigate } from 'react-router-dom';

export default function Homepage() {


  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };
  
  const pageTransition = {
    type: "tween",
    duration: 0.4,
  };

const { scrollY } = useScroll();
const y1 = useTransform(scrollY, [0, 500], [0, 50]);
const y2 = useTransform(scrollY, [0, 500], [0, 100]);

const navigate = useNavigate();

const stats = [
  { label: 'Journal Articles', value: 100000000, icon: <FaNewspaper /> },
  { label: 'Journals', value: 90000, icon: <FaBookOpen /> },
  { label: 'Theses', value: 1200000, icon: <FaFileAlt /> },
  { label: 'Pre-Prints', value: 2200000, icon: <FaPenFancy /> },
  { label: 'Book Chapters', value: 200000, icon: <FaLayerGroup /> },
  { label: 'eBooks', value: 100000, icon: <FaTabletAlt /> },
  { label: 'Multimedia', value: 120000, icon: <FaVideo /> },
  { label: 'Conference Papers', value: 220000, icon: <FaFileSignature /> },
];

const features = [
  {
    title: "Unified Discovery",
    icon: <FaSearch />,
    desc: "Search and access across eBooks, Journals, LMS, and more in one place.",
    slug: "unified-discovery",
  },
  {
    title: "AI-Powered Search",
    icon: <FaRobot />,
    desc: "Smart suggestions and relevance-based results using AI.",
    slug: "ai-powered-search",
  },
  {
    title: "API Integration",
    icon: <FaSatelliteDish />,
    desc: "Real-time connection with 150+ publishers, KOHA, DSpace, J-Gate, etc.",
    slug: "api-integration",
  },
  {
    title: "Mobile App Access",
    icon: <FaMobileAlt />,
    desc: "iDisKover is available on Android and iOS platforms.",
    slug: "mobile-app",
  },
  {
    title: "Secure Authentication",
    icon: <FaShieldAlt />,
    desc: "Supports IP, SSO, LDAP, OAuth, SAML, and Social logins.",
    slug: "secure-authentication",
  },
  {
    title: "Admin Dashboard",
    icon: <FaChartBar />,
    desc: "Manage users, resources, subscriptions, and generate reports easily.",
    slug: "admin-dashboard",
  },
  {
    title: "White-Labeled Portal",
    icon: <FaPalette />,
    desc: "Fully branded platform tailored to your institution’s identity.",
    slug: "white-label",
  },
  {
    title: "Enterprise-Grade Security",
    icon: <FaGlobe />,
    desc: "Complies with GDPR, ISO standards with anomaly detection.",
    slug: "security-compliance",
  },
  {
    title: "Institutional Repository",
    icon: <FaDatabase />,
    desc: "Get 2TB of shared DSpace storage for digital archives.",
    slug: "institutional-repository",
  },
];


const testimonials = [
  {
    name: "Dr. Kavita Rao",
    role: "Librarian, City University",
    quote: "iDisKover has transformed the way our students access academic content. The unified search and mobile accessibility are game-changers!",
    avatar: "/testimonials/user1.jpg"
  },
  {
    name: "Prof. Arjun Mehta",
    role: "Dean of Research, Tech Institute",
    quote: "With AI-powered search and vast integrations, iDisKover became our go-to solution for digital library needs.",
    avatar: "/testimonials/user2.png"
  },
  {
    name: "Dr. Nisha Jain",
    role: "Head Librarian, Medilore College",
    quote: "The customization and analytics dashboard is fantastic. We get powerful insights with ease.",
    avatar: "/testimonials/user3.jpg"
  }
];


  return (
    <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
<>
    <Helmet>
      <title>iDisKover - Digital Library Platform</title>
      <meta name="description" content="Explore the next-gen digital library platform with intelligent search, rich resources, and seamless access." />
    </Helmet>

    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <section id="home" className="bg-gradient-to-r from-white via-[#eff3f5] to-[#d0d8da] pt-20 md:pt-0 pb-10 md:pb-2 px-6">
        {/* Background bubbles */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <svg className="absolute top-10 left-10 w-40 h-40 opacity-20 animate-bounce-slow" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="50" fill="#ff7a00" />
          </svg>
          <svg className="absolute top-32 right-0 w-32 h-32 opacity-10 animate-pulse-slow" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="50" fill="#066a75" />
          </svg>
          <svg className="absolute bottom-10 left-20 w-24 h-24 opacity-20 animate-float" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="50" fill="#4ecdc4" />
          </svg>

          {/* Top right blurred shape */}
          <div className="absolute top-20 right-16 w-72 h-72 bg-[#4ecdc4] opacity-10 rounded-full blur-2xl animate-bounce-slow" />

          {/* Bottom left */}
          <div className="absolute bottom-10 left-12 w-52 h-52 bg-[#066a75] opacity-20 rounded-full blur-2xl animate-float" />
        </div>

        <motion.div
          style={{ y: y1 }}
          className="absolute top-16 left-10 w-64 h-64 bg-[#ff7a00] opacity-10 blur-3xl rounded-full"
        />

        <motion.div
          style={{ y: y2 }}
          className="absolute top-16 left-10 w-64 h-64 bg-[#ff7a00] opacity-10 blur-3xl rounded-full"
        />


        
        <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center">
          {/* Left Text Column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-[#055862] mb-4 leading-snug" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
              <Typewriter
                options={{
                  strings: [
                    'Explore the Next-Gen Digital Library',
                    'Discover Books, Journals & Knowledge',
                    'Search. Learn. Grow.'
                  ],
                  autoStart: true,
                  loop: true,
                  delay: 50,
                  deleteSpeed: 30,
                }}
              />
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Seamlessly search, access, and engage with thousands of books and journals across all subject categories.
            </p>
            <a
              href="#stats"
              className="inline-block bg-[#ff7a00] hover:bg-[#e06a00] text-white px-6 py-3 rounded-lg font-medium transition shadow-lg hover:scale-105"
            >
              Login to Explore Content
            </a>
          </motion.div>

          {/* Right Image Column */}
          <motion.img
            src="/banner.png"
            alt="Digital Library Banner"
            className="w-full max-w-md mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          />
        </div>
      </section>

      {/* Features Snippet */}
      <section id="features" className="bg-white py-20 px-6">
        <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-center text-[#055862] mb-12" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
          Key Features
        </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="bg-[#f9fafb] p-6 rounded-xl shadow transition-all duration-300 ease-in-out hover:-translate-y-1 hover:bg-[#fef8f3] hover:shadow-[0_8px_30px_rgba(255,122,0,0.15)] cursor-pointer"
              onClick={() => navigate(`/features#${feature.slug}`)}
            >
              <div className="text-4xl mb-4 text-[#ff7a00]">{feature.icon}</div>
              <h4 className="text-xl font-semibold text-[#055862] mb-2">{feature.title}</h4>
              <p className="text-gray-600">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

          {/* Stat block Snippet */}
          <section id="stats" className="bg-gradient-to-r from-[#055862] to-[#ff7a00] text-white py-12">
            <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-center">
                {stats.map((stat, idx) => (
                  <div key={idx} className="group flex flex-col items-center justify-center gap-3 p-6 rounded-xl backdrop-blur-sm bg-white/10 border border-white/20 shadow-xl hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition duration-300 cursor-pointer">
                    <div className="text-5xl text-white group-hover:scale-110 group-hover:-rotate-3 transition duration-300">{stat.icon}</div>
                    <div className="text-3xl font-bold">
                      <CountUp end={stat.value} duration={2.5} separator="," enableScrollSpy scrollSpyDelay={100} />+
                    </div>
                    <p className="text-lg">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>


      {/* Contact Us Snippet */}
      <section id="contact" className="bg-gradient-to-br from-white to-gray-100 py-20 px-6">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-center gap-10 max-w-6xl">

        {/* LEFT BOX */}
        <div className="bg-white rounded-xl shadow-md p-8 flex-1">
          <h3 className="text-xl font-bold text-[#055862] mb-4">24/7 Client Support</h3>
          <p className="text-gray-600 mb-6">
            We're always available to support your institution and users. From setup to scaling, our team is here to help.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-gradient-to-r from-[#055862] to-[#ff7a00] text-white px-6 py-3 rounded-md shadow hover:opacity-90 transition"
          >
            Contact Us
          </button>
        </div>

        {/* ICON IN CENTER */}
        <div className="rounded-full border-4 border-[#ff7a00] p-6 bg-white shadow-lg">
          <FaHeadset className="text-5xl text-[#055862]" />
        </div>

        {/* RIGHT BOX */}
        <div className="bg-white rounded-xl shadow-md p-8 flex-1">
          <h3 className="text-xl font-bold text-[#055862] mb-4">100% Client Satisfaction</h3>
          <p className="text-gray-600 mb-6">
            Your feedback drives our innovation. We're committed to delivering a reliable, seamless experience.
          </p>
          <a
            href="/contact"
            className="border border-[#055862] text-[#055862] px-6 py-3 rounded-md font-medium hover:bg-[#ff7a00] hover:text-white transition"
          >
            Contact Us
          </a>
        </div>

      </div>
    </section>


      {/* About Us Snippet */}
      <section id="about" className="bg-gradient-to-r from-[#055862] to-[#ff7a00] text-white py-20 px-6">
      <div className="container mx-auto grid md:grid-cols-2 items-center gap-12">
        
        {/* LEFT: Textual Content */}
        <div>
          <button className="px-4 py-1 mb-4 border border-white rounded-full text-sm uppercase tracking-wide">
            About Informatics
          </button>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Powering the Future of Knowledge Access.
          </h2>
          <div className="flex items-center gap-8 mb-8">
          <div className="bg-white text-[#ff7a00] px-6 py-4 rounded-lg text-center shadow-lg animate-float">
            <div className="text-3xl font-bold flex justify-center items-center gap-1">
              45 <FaArrowUp className="text-sm" />
            </div>
            <p className="text-sm font-medium mt-1">Years Experience</p>
          </div>
            <div>
              <h4 className="text-xl font-semibold underline mb-2">Our Vision</h4>
              <p className="text-white/90">
              To be a leading global player in information management and publishing space with commitment to excellence.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-semibold underline mb-2">Our Business</h4>
              <p className="text-white/90">
              Working with information and knowledge to create value to our customers.
              </p>
            </div>
          </div>
          <a
            href="about"
            className="inline-block bg-white text-[#055862] font-bold px-6 py-3 rounded-lg shadow hover:bg-gray-100 transition"
          >
            Learn More About Us
          </a>
        </div>

        {/* RIGHT: Image */}
        <div className="relative">
          <img
            src="/banner2.png"
            alt="About us illustration"
            className="rounded-[120px] border-4 border-white shadow-2xl"
          />
        </div>
      </div>
    </section>


    {/* Testimonials Snippet */}
    <section className="bg-gray-50 py-20 px-4">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl font-bold text-[#055862] mb-4" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>What Our Clients Say</h2>
        <p className="text-gray-600 mb-12 max-w-xl mx-auto">
          Trusted by institutions and educators across the globe.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.2 }}
              className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition relative"
            >
              <FaQuoteLeft className="text-[#ff7a00] text-2xl mb-4" />
              <p className="text-gray-700 italic mb-6">"{item.quote}"</p>
              <div className="flex items-center gap-4">
                <img
                  src={item.avatar}
                  alt={item.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#055862]"
                />
                <div className="text-left">
                  <h4 className="font-semibold text-[#055862]">{item.name}</h4>
                  <span className="text-sm text-gray-500">{item.role}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    </div>
    </>
    </motion.div>
  );
}
